import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Button } from '@/shared/components/ui/button';
import { EfficiencyBarChart } from './EfficiencyBarChart';
import { GainLossBarChart } from './GainLossBarChart';
import { EfficiencyTrendChart } from './EfficiencyTrendChart';
import type { MemberTableEntry, MemberTimelineEntry } from '../types';

interface PerformanceChartViewProps {
  tableEntries: MemberTableEntry[];
  timelineEntries: MemberTimelineEntry[];
  periodStart: string;
  periodEnd: string;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
}

export function PerformanceChartView({
  tableEntries,
  timelineEntries,
  periodStart,
  periodEnd,
  isLoading = false,
  isError = false,
  onRetry,
}: PerformanceChartViewProps) {
  const { t } = useTranslation('laborPerformance');

  if (isLoading) {
    return (
      <div
        className="grid grid-cols-1 gap-4 md:grid-cols-2"
        data-testid="labor-performance-chart-view-loading"
      >
        <Skeleton className="h-[276px] w-full rounded-lg" />
        <Skeleton className="h-[276px] w-full rounded-lg" />
        <Skeleton className="col-span-1 h-[276px] w-full rounded-lg md:col-span-2" />
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className="rounded-lg border border-red-200 bg-red-50 p-4"
        data-testid="labor-performance-chart-view-error"
      >
        <p className="text-sm font-medium text-red-800">{t('states.error')}</p>
        {onRetry && (
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={onRetry}
            data-testid="labor-performance-chart-view-retry"
          >
            {t('states.retry')}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="labor-performance-chart-view-container">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <EfficiencyBarChart entries={tableEntries} />
        <GainLossBarChart entries={tableEntries} />
      </div>
      <EfficiencyTrendChart
        entries={timelineEntries}
        periodStart={periodStart}
        periodEnd={periodEnd}
      />
    </div>
  );
}
